import {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import _ from 'lodash';
import {
  addToTrip, removeFromTrip, saveTripRequest, setSelfBookingAllowed,
} from '../../../../store/slices/trip';
import { getCarRateRulesRequest, getCarRateRulesSuccess } from '../../../../store/slices/car';
import CarDetails from '../CarDetails';
import CarItineraryInfo from './CarItineraryInfo/index.tsx';
import { PageLinks, SearchStatus, TravelTypes } from '../../../../constants';
import useScrollToElementById from '../../../../hooks/useScrollToElementById';
import getElementId from '../../../../helpers/getElementId';
import checkDoShowMarkupRates from '../../../../helpers/selectors/checkDoShowMarkupRates';
import checkDoEnableTripConfigDecoupling from '../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';

const CarItinerary = ({
  itinerary, tripId, onModalClose, selectedTab,
  doShowPrice, doHaveBorderAround, tripRoomSearchInput,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const doShowMarkupRate = useSelector(checkDoShowMarkupRates({
    mode: TravelTypes.carrental, tripId, itineraryId: selectedTab,
  }));
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const { pageName, segment: itineraryIdToUpdate } = router.query;
  const doAddChangeCarBookedTrip = pageName === PageLinks.ChangeCar.pageName
    || pageName === PageLinks.AddCar.pageName;
  const doCheckoutOnSelect = doAddChangeCarBookedTrip;
  const { selectedTrips } = useSelector((store) => store.trip);
  const trip = selectedTrips && selectedTrips[tripId];
  const saveTripStatus = trip && trip.saveTripStatus;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const { travelAgency = {} } = useSelector((state) => state.user);
  const brochureSettings = travelAgency?.settings?.brochureSettings || {};
  const doExpandItineraryDetailsByDefault = !!brochureSettings?.doExpandItineraryDetailsByDefault
  && isBrochurePage;
  const [isDetailsOpen, setIsDetailsOpen] = useState(doExpandItineraryDetailsByDefault);
  const { track } = useAnalytics();
  const isSelected = useMemo(() => (trip
    && trip.rentalCarItineraries
    && trip.rentalCarItineraries[selectedTab]
    && trip.rentalCarItineraries[selectedTab].uniqueKey === itinerary.uniqueKey) === true,
  [selectedTab, trip, itinerary]);

  const carRateOptionRules = useSelector((store) => store.car.carRateOptionRules);
  const {
    getCarRateRulesStatus, vendorMessages,
  } = carRateOptionRules[itinerary.uniqueKey] || {};
  const scrollToElement = useScrollToElementById();

  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);

  useEffect(() => {
    if (itinerary && Object.keys(itinerary.vendorMessages || {}).length > 0) {
      dispatch(getCarRateRulesSuccess({
        key: itinerary.uniqueKey,
        vendorMessages: itinerary.vendorMessages,
      }));
    }
  }, [itinerary, dispatch]);

  const handleDetailsClick = () => {
    setIsDetailsOpen((v) => !v);
    if (!vendorMessages || Object.keys(vendorMessages || {}).length === 0) {
      dispatch(getCarRateRulesRequest({
        key: itinerary.uniqueKey, itinerary,
      }));
    }
  };

  const handleRemoveFromTrip = useCallback(() => {
    dispatch(removeFromTrip({ tripId, key: 'rentalCarItineraries', id: itinerary.id }));
  }, [dispatch, tripId, itinerary.id]);

  const handleScroll = useCallback((event) => {
    if (!event) return;
    if (doEnableTripConfigDecoupling) {
      const containerWrapper = document.getElementById('main-section-wrapper');
      containerWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const elementId = getElementId(event, '.parentDiv');
    if (elementId) {
      scrollToElement(elementId, 'banner');
    } else {
      scrollToElement(tripId, 'banner');
    }
  }, [doEnableTripConfigDecoupling, scrollToElement, tripId]);

  const handleAddToTripClick = useCallback((event) => {
    if (tripId) {
      let newRentalCarItineraries = {};
      if (trip && trip.rentalCarItineraries) {
        newRentalCarItineraries = { ...trip.rentalCarItineraries };
      }
      dispatch(addToTrip({
        tripId,
        trip: {
          rentalCarItineraries: {
            ...newRentalCarItineraries,
            [selectedTab]: {
              ...itinerary,
              vendorMessages,
              id: parseInt(selectedTab, 10),
              cost: {
                ...itinerary.cost,
                doShowMarkupAmount: doShowMarkupRate
                  && itinerary.cost.price !== itinerary.cost.markupPrice,
              },
            },
          },
        },
      }));
      if (onModalClose) onModalClose();
      handleScroll(event);
    }
    track('carItinerary.handleAddToTripClick');
  }, [dispatch, itinerary, onModalClose, selectedTab, trip, tripId, track, vendorMessages,
    handleScroll, doShowMarkupRate]);

  const handleCheckoutOnClick = useCallback(() => {
    if (isBrochurePage) {
      dispatch(setSelfBookingAllowed());
    }
    if (saveTripStatus === SearchStatus.SEARCHING) return;

    const rentalCarItinerary = trip && trip.rentalCarItineraries
      && _.find(trip.rentalCarItineraries, (car) => car.id === +(itineraryIdToUpdate || 0));
    const request = {
      tripId,
      trip: {
        tripId,
        doAddItineraryInBookedTrip: pageName === PageLinks.AddCar.pageName,
        doChangeItineraryInBookedTrip: pageName === PageLinks.ChangeCar.pageName,
        rentalCarItineraries: [{
          ...itinerary,
          vendorMessages,
          id: +(itineraryIdToUpdate || 0),
          tripSegmentId: rentalCarItinerary && rentalCarItinerary.tripSegmentId,
        }],
      },
      tripRoomSearchInput,
    };
    if (onModalClose) onModalClose();
    dispatch(saveTripRequest(request));
    track('carItinerary.handleCheckoutOnClick');
  }, [isBrochurePage, saveTripStatus, trip, tripId,
    pageName, itinerary, vendorMessages, itineraryIdToUpdate,
    tripRoomSearchInput, onModalClose, dispatch, track]);

  return (
    <div className={`my-3 rounded-lg sm:p-3 p-2
    ${clsx({
      'border-theme border-opacity-50 bg-theme bg-opacity-10': doHaveBorderAround && isSelected,
      'border-2': doHaveBorderAround,
    })}`}
    >
      <CarItineraryInfo
        tripId={tripId}
        itinerary={itinerary}
        selectedTab={selectedTab}
        doShowPrice={doShowPrice}
        doCheckoutOnSelect={doCheckoutOnSelect}
        onCheckoutOnClick={handleCheckoutOnClick}
        onAddToTripClick={handleAddToTripClick}
        isSelected={isSelected}
        onRemoveFromTrip={handleRemoveFromTrip}
        onDetailsClick={doShowPrice ? handleDetailsClick : undefined}
        isDetailsOpen={isDetailsOpen}
        isViewingBrochure={isViewingBrochure}
        isViewingCompare={isViewingCompare}
        doShowMarkupRate={doShowMarkupRate}
        tripRoomSearchInput={tripRoomSearchInput}
      />
      {isDetailsOpen && (
        <CarDetails
          itinerary={itinerary}
          getCarRateRulesStatus={getCarRateRulesStatus}
          vendorMessages={vendorMessages}
          doShowMarkupRate={doShowMarkupRate}
        />
      )}
    </div>
  );
};

CarItinerary.propTypes = {
  selectedTab: PropTypes.number,
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  tripId: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowPrice: PropTypes.bool,
  doHaveBorderAround: PropTypes.bool,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

CarItinerary.defaultProps = {
  selectedTab: 0,
  itinerary: {},
  tripId: '',
  onModalClose: undefined,
  doShowPrice: true,
  doHaveBorderAround: true,
  tripRoomSearchInput: undefined,
};

export default memo(CarItinerary);
